import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import slidersIcon from '@/shared/assets/icons/sliders.svg';
import dashboardIcon from '@/shared/assets/icons/dashboard.svg';
import type { RecommendedPresentationVm } from '../viewModels/newHome.vm';
import { ProductCard } from '@/features/plp/presentation/components/ProductCard';
import type { ListingDesignItem, ListingFilter } from '@/features/plp/domain/models/ProductListing';
import type { RecommendedBrowsePageResult } from '@/features/newHome/application/services/NewHomeService';
import type { FiltersService, GetFiltersParams } from '@/features/filters/application/services/FiltersService';
import type { PlpFiltersResult } from '@/features/filters/domain/models/Filter';
import { PLP_PAGE_TYPE } from '@/features/plp/constants';
import { useGetMenuQuery } from '@/shared/features/menu/infra/adapters/menuApiSlice';
import type { MenuSubCategory } from '@/shared/features/menu/domain/models/MenuItem';
import { cn } from '@/shared/utils/cn';
import { TAILWIND_LG_MIN_PX, TAILWIND_MD_MIN_PX } from '@/shared/constants/breakpoints';
import { useRecommendedBrowseUrlFilters, tagToUrlSegment } from '../hooks/useRecommendedBrowseUrlFilters';
import { useRecommendedBrowseFacetFilters } from '../hooks/useRecommendedBrowseFacetFilters';
import { RecommendedBrowseFilterSheet } from './RecommendedBrowseFilterSheet';
import { RecommendedBrowseFiltersPanel } from './RecommendedBrowseFiltersPanel';
import { getJewelleryBrowseTagStringFromApplyPath } from '@/features/newHome/utils/jewelleryBrowsePath';
import { NH_RECOMMENDED_CATEGORY_STRIP_SENTINEL_ID } from '@/features/newHome/utils/recommendedCategoryStripSentinel';
import { useRecommendedFilterStripStuckSync } from '../hooks/useRecommendedFilterStripStuckSync';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useAppSelector, useAppDispatch } from '@/app/store/store';
import { selectFooterInViewport } from '@/app/store/footerVisibilitySlice';
import {
  selectMobileRecommendedFilterStripStuck,
  setRecommendedBrowseFiltersEnabled,
} from '@/app/store/newHomeUiSlice';
import {
  buildSectionTitleStyle,
  sectionTitleFontFamilyClass,
  sectionTitleFontWeightClass,
} from './sectionTitleUtils';
import { useSelector } from 'react-redux';
import { selectPlpInitialData } from '@/features/plp/presentation/store/plpInitialSlice';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HOME_PAGE_PLP_FILTER_CLICKED,
  homePagePlpFilterClickedEventProperties,
} from '../analytics/homePagePlpFilterClicked';
import { HOME_PAGE_PLP_FILTER_APPLIED } from '../analytics/homePagePlpFilterApplied';
import { buildFilterAppliedEventPayload } from '@/features/filters/utils/buildFilterAppliedEventPayload';
import {
  HOME_PAGE_CATEGORY_FILTER_SELECTED,
  homePageCategoryFilterSelectedEventProperties,
} from '../analytics/homePageCategoryFilterSelected';
import {
  getHomePlpProductClick,
  HOME_PAGE_PLP_PRODUCT_CLICKED,
  HOME_PAGE_PLP_ADDED_TO_WISHLIST,
  HOME_PAGE_PLP_ADDED_TO_VIDEO_CALL_CART,
  HOME_PAGE_PLP_PRODUCT_VIEWED,
  getHomePlpAddedToWishlist,
  getHomePlpAddedToVideoCallCart,
  getHomePlpProductViewed,
} from '../analytics/homePagePlp';
import { PlpProductViewTracker } from '@/features/plp/presentation/components/PlpProductViewTracker';
interface RecommendedSectionProps {
  data: RecommendedPresentationVm;
  eventLabel: string;
  getRecommendedProducts: (
    tags: string,
    pageNumber: number,
    options?: { isFiltered?: boolean; signal?: AbortSignal }
  ) => Promise<RecommendedBrowsePageResult>;
  fetchBrowseFilterPreview: (params: {
    tags: string;
    tags_to_sort_filters?: string;
  }) => Promise<PlpFiltersResult>;
}

function pathnameFromApplyUrl(applyUrl: string): string {
  const raw = applyUrl.trim();
  if (!raw) return '';
  if (raw.startsWith('http')) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw.split('?')[0];
    }
  }
  const path = raw.split('?')[0];
  return path.startsWith('/') ? path : `/${path}`;
}

/** Derive browse tag from a menu sub-category link. e.g. "/jewellery/rings.html" → "rings" */
function tagFromLink(link?: string): string {
  if (!link) return '';
  const cleaned = link.replace(/\/+$/, '').replace(/\.html?$/i, '');
  const segments = cleaned.split('/');
  return segments[segments.length - 1] ?? '';
}

interface CategoryTab {
  label: string;
  tag: string;
  imageUrl: string;
}

/** Extract category tabs from the hamburger menu API data */
function useCategoryTabs(): CategoryTab[] {
  const { data } = useGetMenuQuery(undefined, { refetchOnMountOrArgChange: false });
  const hamburger = data?.hamburger ?? [];

  // Find the dropdown that contains jewellery categories (e.g. "Shop For")
  // and pick the "Women" category's sub-categories (Rings, Earrings, etc.)
  for (const item of hamburger) {
    if (item.view_type !== 'dropdown' || !item.categories) continue;
    for (const cat of item.categories) {
      if (!cat.sub_categories?.length) continue;
      // Check if this category contains typical jewellery sub-categories with images
      const withImages = cat.sub_categories.filter((sc: MenuSubCategory) => sc.image_url);
      if (withImages.length >= 3) {
        return withImages.map((sc: MenuSubCategory) => ({
          label: sc.title,
          tag: tagFromLink(sc.link),
          imageUrl: sc.image_url ?? '',
        }));
      }
    }
  }
  return [];
}

function CategoryFilterTab({
  tab,
  isAll,
  isActive,
  onSelect,
}: {
  tab: CategoryTab;
  isAll: boolean;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex flex-col items-center gap-1 shrink-0"
      style={{ width: 64 }}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full overflow-hidden transition-colors',
          isActive ? 'border-2 border-rose-400' : 'border border-gray-200'
        )}
        style={{ width: 56, height: 56 }}
      >
        {isAll ? (
          <div className="flex items-center justify-center w-full h-full bg-white">
            <img src={dashboardIcon} alt="" className="h-7 w-7" width={28} height={28} />
          </div>
        ) : (
          <img
            src={tab.imageUrl}
            alt={tab.label}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <span
        className={cn(
          'text-[12px] leading-tight text-center',
          isActive ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'
        )}
      >
        {tab.label}
      </span>
    </button>
  );
}

function BrowseAllFiltersTab({
  disabled,
  isOpen,
  onToggle,
}: {
  disabled: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      className="flex flex-col items-center gap-1 shrink-0 disabled:opacity-40"
      style={{ width: 64 }}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-label={isOpen ? 'Close filters' : 'Open all filters'}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full overflow-hidden bg-white transition-colors',
          isOpen ? 'border-2 border-[#0c2a4a]' : 'border border-gray-200'
        )}
        style={{ width: 56, height: 56 }}
      >
        <img src={slidersIcon} alt="" className="h-7 w-7" width={28} height={28} aria-hidden />
      </div>
      <span
        className={cn(
          'text-[10px] leading-tight text-center',
          isOpen ? 'font-semibold text-[#0c2a4a]' : 'font-normal text-[#0c2a4a]'
        )}
      >
        Filters
      </span>
    </button>
  );
}

/** Per-tag cached state for infinite scroll */
interface TagCache {
  designs: ListingDesignItem[];
  currentPage: number;
  totalCount: number;
  hasMore: boolean;
  filters: ListingFilter[];
  filtersResetUrl?: string;
}

export default function RecommendedSection({
  data,
  eventLabel,
  getRecommendedProducts,
  fetchBrowseFilterPreview,
}: RecommendedSectionProps) {
  const { analytics, isInitialized } = useAnalytics();
  const trackProductClick = useCallback(
    (product: ListingDesignItem, scrollDepth?: number, horizontalPosition?: number) => {
      if (analytics) {
        try {
          analytics.trackEvent(HOME_PAGE_PLP_PRODUCT_CLICKED, getHomePlpProductClick(product, scrollDepth, horizontalPosition));
        } catch (err) {
          console.error('Error tracking Home Page PLP Product Clicked:', err);
        }
      }
    },
    [analytics]
  );
  const menuTabs = useCategoryTabs();
  const allTab: CategoryTab = { label: 'All', tag: '', imageUrl: '' };

  const configTabs: CategoryTab[] = (data.categoryFilters ?? [])
    .filter((f) => f.label?.trim())
    .map((f) => ({
      label: f.label.trim(),
      tag: f.tag ?? '',
      imageUrl: f.imageUrl ?? '',
    }));

  const desiredOrder = ['Earrings', 'Bracelets', 'Pendants', 'Rings', 'Bangles'];
  const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const byLabel = new Map(menuTabs.map((t) => [normalize(t.label), t]));
  const orderedTabs = desiredOrder
    .map((label) => byLabel.get(normalize(label)))
    .filter((t): t is CategoryTab => Boolean(t));

  const baseTabs =
    configTabs.length > 0 ? configTabs : orderedTabs.length > 0 ? orderedTabs : menuTabs;
  const tabs = baseTabs.length > 0 ? [allTab, ...baseTabs] : [];

  const allowedTags = useMemo(
    () => baseTabs.map((t) => t.tag).filter((tag) => tag.trim().length > 0),
    [baseTabs]
  );

  const {
    listingTagString,
    listingIsFiltered,
    selectedTag,
    selectTag,
    clearAll,
    setFiltersFromBrowseCommit,
    isTagSelected,
    isAllSelected,
  } = useRecommendedBrowseUrlFilters({
    allowedTags,
    defaultBrowseTags: data.browseTags ?? '',
  });
  const isServer = typeof window === 'undefined';
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const mobileFilterStripStuck = useAppSelector(selectMobileRecommendedFilterStripStuck);
  const recommendedBrowseFilterNavNonce = useAppSelector(
    (s) => s.newHomeUi?.recommendedBrowseFilterButtonNonce ?? 0
  );
  const lastFilterNavNonce = useRef(0);
  const filterAnchorRef = useRef<HTMLDivElement | null>(null);

  const footerInViewport = useAppSelector(selectFooterInViewport);
  const footerInViewportRef = useRef(footerInViewport);
  useEffect(() => {
    footerInViewportRef.current = footerInViewport;
  }, [footerInViewport]);
  const recommendedLoadMoreAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!footerInViewport) return;
    recommendedLoadMoreAbortRef.current?.abort();
  }, [footerInViewport]);

  const filterPreviewBaseTags = useMemo(
    () => (selectedTag != null ? tagToUrlSegment(selectedTag) : (data.browseTags ?? '').replace(/ /g, '+')),
    [selectedTag, data.browseTags]
  );

  const listingDataKey = useMemo(
    () => `${listingTagString}\x1e${listingIsFiltered ? '1' : '0'}`,
    [listingTagString, listingIsFiltered]
  );

  const commitFacetFromApplyUrl = useCallback(
    (applyUrl: string | null) => {
      if (applyUrl == null || applyUrl === '') {
        setFiltersFromBrowseCommit(null, false);
        return;
      }
      const path = pathnameFromApplyUrl(applyUrl);
      if (!path) {
        setFiltersFromBrowseCommit(null, false);
        return;
      }
      const tags = getJewelleryBrowseTagStringFromApplyPath(path);
      const isFiltered = path.includes('/filter/');
      const categorySeg = selectedTag != null ? tagToUrlSegment(selectedTag) : '';
      const matchesCategoryOnly = !isFiltered && selectedTag != null && tags === categorySeg;
      if (matchesCategoryOnly) {
        setFiltersFromBrowseCommit(categorySeg, false);
      } else {
        setFiltersFromBrowseCommit(tags, isFiltered);
      }
    },
    [selectedTag, setFiltersFromBrowseCommit]
  );

  const initialProducts = useSelector(selectPlpInitialData);
  const initialCache = useMemo(() => {
  if (!initialProducts?.designs?.length) return {};
    return {
      [listingDataKey]: {
        designs: initialProducts.designs,
        currentPage: 1,
        totalCount: initialProducts.totalCount,
        hasMore: initialProducts.totalCount > initialProducts.designs.length,
        filters: initialProducts.filters ?? [],
        filtersResetUrl: initialProducts.filtersResetUrl,
      } as TagCache,
    };
  }, [
    initialProducts?.designs,
    initialProducts?.filters,
    initialProducts?.filtersResetUrl,
    initialProducts?.totalCount,
    listingDataKey
  ]);
  const [products, setProducts] = useState<ListingDesignItem[]>(initialProducts?.designs || []);
  const [isLoading, setIsLoading] = useState(initialProducts?.designs?.length === 0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listingFiltersSnapshot, setListingFiltersSnapshot] = useState<ListingFilter[]>(
    initialProducts?.filters || []
  );
  const cacheRef = useRef<Record<string, TagCache>>(initialCache);
  const [filtersResetUrlSnapshot, setFiltersResetUrlSnapshot] = useState<string | undefined>(
    initialProducts?.filtersResetUrl || undefined
  );
  const [hasMore, setHasMore] = useState(
    initialProducts ? initialProducts.totalCount > (initialProducts.designs?.length ?? 0) : false
  );
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [gridCols, setGridCols] = useState<2 | 3 | 4>(() => {
    if (typeof window === 'undefined') return 2;
    if (window.matchMedia(`(min-width: ${TAILWIND_LG_MIN_PX}px)`).matches) return 4;
    if (window.matchMedia(`(min-width: ${TAILWIND_MD_MIN_PX}px)`).matches) return 3;
    return 2;
  });
  const viewedProductIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    viewedProductIds.current.clear();
  }, [listingDataKey]);

  const trackWishlistToggle = useCallback(
    (product: ListingDesignItem, action: 'add' | 'remove') => {
      if (action !== 'add') return;
      if (analytics) {
        try {
          analytics.trackEvent(
            HOME_PAGE_PLP_ADDED_TO_WISHLIST,
            getHomePlpAddedToWishlist(product)
          );
        } catch (err) {
          console.error('Error tracking Home Page PLP Added to Wishlist:', err);
        }
      }
    },
    [analytics]
  );

  const trackVideoCallCartAdd = useCallback(
    (product: ListingDesignItem, errorMsg?: string) => {
      if (analytics) {
        try {
          analytics.trackEvent(
            HOME_PAGE_PLP_ADDED_TO_VIDEO_CALL_CART,
            getHomePlpAddedToVideoCallCart(product, errorMsg)
          );
        } catch (err) {
          console.error('Error tracking Home Page PLP Added to Video Call Cart:', err);
        }
      }
    },
    [analytics]
  );

  const trackProductViewed = useCallback(
    (params: {
      product: ListingDesignItem;
      scrollDepth: number;
      horizontalPosition: number;
    }) => {
      if (analytics) {
        try {
          analytics.trackEvent(
            HOME_PAGE_PLP_PRODUCT_VIEWED,
            getHomePlpProductViewed(params.product, params.scrollDepth, params.horizontalPosition)
          );
        } catch (err) {
          console.error('Error tracking Home Page PLP Product Viewed:', err);
        }
      }
    },
    [analytics]
  );

  const showFiltersForSSR = isServer && listingFiltersSnapshot.length > 0;

  useRecommendedFilterStripStuckSync(
    stickySentinelRef,
    tabs.length,
    listingDataKey,
    isLoading,
    products.length,
  );

  const filterPreviewService = useMemo(
    () =>
      ({
        getFilters: (params: GetFiltersParams) => {
          if (params.page_type === PLP_PAGE_TYPE.SEARCH) {
            return Promise.resolve({ filters: [] });
          }
          return fetchBrowseFilterPreview({
            tags: params.tags,
            tags_to_sort_filters: params.tags_to_sort_filters,
          });
        },
      }) as unknown as FiltersService,
    [fetchBrowseFilterPreview]
  );

  const trackHomePagePlpFilterApplied = useCallback(
    (ctx: { previewFilters: ListingFilter[]; selectedOptions: Record<string, string[]> }) => {
      if (!isInitialized || !analytics) return;
      const payload = buildFilterAppliedEventPayload(ctx.previewFilters, ctx.selectedOptions);
      if (!payload) return;
      try {
        analytics.trackEvent(HOME_PAGE_PLP_FILTER_APPLIED, payload);
      } catch (err) {
        console.error('Error tracking Home Page PLP Filter Applied:', err);
      }
    },
    [analytics, isInitialized],
  );

  const {
    isOpen: isBrowseFilterSheetOpen,
    toggleFilterSheet: toggleBrowseFilterSheet,
    previewFilters: browsePreviewFilters,
    selectedOptions: browseSelectedOptions,
    applyUrl: browseApplyUrl,
    isLoadingPreview: isBrowseFilterPreviewLoading,
    isPendingCommit: isBrowseFacetPendingCommit,
    toggleOption: browseToggleOption,
    handleApply: handleBrowseFilterApply,
    handleReset: handleBrowseFilterReset,
    handleClose: handleBrowseFilterClose,
  } = useRecommendedBrowseFacetFilters({
    listingFilters: listingFiltersSnapshot,
    filtersResetUrl: filtersResetUrlSnapshot,
    baseTags: filterPreviewBaseTags,
    isDesktop,
    isListingLoading: isLoading,
    service: filterPreviewService,
    onCommit: commitFacetFromApplyUrl,
    onApplyCommitted: trackHomePagePlpFilterApplied,
  });

  const trackAndToggleBrowseFilterSheet = useCallback(() => {
    if (!isBrowseFilterSheetOpen) {
      if (isInitialized && analytics) {
        try {
          analytics.trackEvent(
            HOME_PAGE_PLP_FILTER_CLICKED,
            homePagePlpFilterClickedEventProperties(),
          );
        } catch (err) {
          console.error('Error tracking Home Page PLP Filter Clicked:', err);
        }
      }
    }
    toggleBrowseFilterSheet();
  }, [analytics, isBrowseFilterSheetOpen, isInitialized, toggleBrowseFilterSheet]);

  useEffect(() => {
    dispatch(setRecommendedBrowseFiltersEnabled(listingFiltersSnapshot.length > 0));
    return () => {
      dispatch(setRecommendedBrowseFiltersEnabled(false));
    };
  }, [dispatch, listingFiltersSnapshot.length]);

  useEffect(() => {
    if (recommendedBrowseFilterNavNonce === 0) return;
    if (recommendedBrowseFilterNavNonce === lastFilterNavNonce.current) return;
    lastFilterNavNonce.current = recommendedBrowseFilterNavNonce;
    trackAndToggleBrowseFilterSheet();
  }, [recommendedBrowseFilterNavNonce, trackAndToggleBrowseFilterSheet]);

  const prevSelectedChipRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevSelectedChipRef.current === null) {
      prevSelectedChipRef.current = selectedTag;
      return;
    }
    if (prevSelectedChipRef.current !== selectedTag) {
      prevSelectedChipRef.current = selectedTag;
      handleBrowseFilterClose();
    }
  }, [selectedTag, handleBrowseFilterClose]);

  useEffect(() => {
    if (!isDesktop || !isBrowseFilterSheetOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isDesktop, isBrowseFilterSheetOpen]);

  useEffect(() => {
    if (!isBrowseFilterSheetOpen || !isDesktop) return;
    /** Capture so we close before other handlers; works for mouse + touch. */
    const onDocPointerDown = (e: PointerEvent) => {
      const el = filterAnchorRef.current;
      if (el && !el.contains(e.target as Node)) {
        handleBrowseFilterClose();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBrowseFilterClose();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isBrowseFilterSheetOpen, isDesktop, handleBrowseFilterClose]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const mqLg = window.matchMedia(`(min-width: ${TAILWIND_LG_MIN_PX}px)`);
    const mqMd = window.matchMedia(`(min-width: ${TAILWIND_MD_MIN_PX}px)`);
    const update = () => {
      if (mqLg.matches) setGridCols(4);
      else if (mqMd.matches) setGridCols(3);
      else setGridCols(2);
    };
    update();
    mqLg.addEventListener('change', update);
    mqMd.addEventListener('change', update);
    return () => {
      mqLg.removeEventListener('change', update);
      mqMd.removeEventListener('change', update);
    };
  }, []);

  useLayoutEffect(() => {
    if (!gridRef.current || typeof window === 'undefined') return;
    const rect = gridRef.current.getBoundingClientRect();
    setScrollMargin(rect.top + window.scrollY);
  }, [listingDataKey, isLoading, products.length, tabs.length]);

  const tagsForBrowseApi = listingTagString;
  const isFilteredBrowse = listingIsFiltered;

  /** Load first page for the active listing key (category URL + optional home-applied facets) */
  const loadInitial = useCallback(async () => {
    const cacheKey = listingDataKey;
    const cached = cacheRef.current[cacheKey];
    if (cached) {
      setProducts(cached.designs);
      setHasMore(cached.hasMore);
      setListingFiltersSnapshot(cached.filters ?? []);
      setFiltersResetUrlSnapshot(cached.filtersResetUrl);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setListingFiltersSnapshot([]);
    setFiltersResetUrlSnapshot(undefined);
    try {
      const { designs, totalCount, filters, filtersResetUrl } = await getRecommendedProducts(
        tagsForBrowseApi,
        1,
        { isFiltered: isFilteredBrowse }
      );
      const tagCache: TagCache = {
        designs,
        currentPage: 1,
        totalCount,
        hasMore: totalCount > designs.length,
        filters,
        ...(filtersResetUrl ? { filtersResetUrl } : {}),
      };
      cacheRef.current[cacheKey] = tagCache;
      setProducts(designs);
      setHasMore(tagCache.hasMore);
      setListingFiltersSnapshot(filters);
      setFiltersResetUrlSnapshot(filtersResetUrl);
    } catch {
      // keep previous products on error
    } finally {
      setIsLoading(false);
    }
  }, [
    listingDataKey,
    tagsForBrowseApi,
    isFilteredBrowse,
    getRecommendedProducts,
  ]);

  /** Load next page (infinite scroll) */
  const loadMore = useCallback(async () => {
    const cached = cacheRef.current[listingDataKey];
    if (!cached || !cached.hasMore || isLoadingMore) return;
    if (footerInViewportRef.current) return;

    const ac = new AbortController();
    recommendedLoadMoreAbortRef.current = ac;
    setIsLoadingMore(true);
    try {
      const nextPage = cached.currentPage + 1;
      const { designs } = await getRecommendedProducts(tagsForBrowseApi, nextPage, {
        isFiltered: isFilteredBrowse,
        signal: ac.signal,
      });
      if (footerInViewportRef.current) return;
      const updatedDesigns = [...cached.designs, ...designs];
      const updatedHasMore = cached.totalCount > updatedDesigns.length;
      cacheRef.current[listingDataKey] = {
        ...cached,
        designs: updatedDesigns,
        currentPage: nextPage,
        hasMore: updatedHasMore,
      };
      setProducts(updatedDesigns);
      setHasMore(updatedHasMore);
    } catch {
      // ignore load-more errors (incl. abort)
    } finally {
      if (recommendedLoadMoreAbortRef.current === ac) {
        recommendedLoadMoreAbortRef.current = null;
      }
      setIsLoadingMore(false);
    }
  }, [
    listingDataKey, 
    tagsForBrowseApi, 
    isFilteredBrowse,
    getRecommendedProducts, 
    isLoadingMore
  ]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (footerInViewportRef.current) return;
        loadMore();
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const handleChipActivate = useCallback(
    (tag: string, categoryName: string) => {
      if (isInitialized && analytics) {
        try {
          analytics.trackEvent(
            HOME_PAGE_CATEGORY_FILTER_SELECTED,
            homePageCategoryFilterSelectedEventProperties({ categoryName }),
          );
        } catch (err) {
          console.error('Error tracking Home Page Category Filter Selected:', err);
        }
      }
      if (!tag) {
        clearAll();
        return;
      }
      selectTag(tag);
    },
    [analytics, clearAll, isInitialized, selectTag],
  );

  const columnCount = gridCols;
  const rowCount = Math.ceil(products.length / columnCount);
  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    // Row height + vertical gap between virtual rows (see paddingBottom on each row).
    estimateSize: () => 332,
    overscan: 4,
    scrollMargin,
  });
  const totalSize = virtualizer.getTotalSize();
  const virtualRows = virtualizer.getVirtualItems();
  const productGridClass = 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
  const showMobileFloatingBarPadding = isMobile && mobileFilterStripStuck;

  return (
    <section
      className="w-full"
      style={{ backgroundColor: data.bgColor, color: data.textColor }}
      data-analytics-section-name={eventLabel}
    >
      <div className="w-full md:px-6">
        {data.title && (
          <h2
            className={cn(
              'nh-section-title px-4 pt-4 pb-2 text-left leading-none tracking-[0.01em] md:px-0 md:pt-2 md:text-center',
              sectionTitleFontWeightClass(data.title.fontWeight),
              sectionTitleFontFamilyClass(data.title),
              data.title.fontStyle === 'italic' && 'italic',
              data.title.desktopColor && 'nh-section-title--desktop-color',
            )}
            style={buildSectionTitleStyle(data.title)}
          >
            {data.title.text}
          </h2>
        )}

        {tabs.length > 0 && (
          <>
            <div
              ref={stickySentinelRef}
              id={NH_RECOMMENDED_CATEGORY_STRIP_SENTINEL_ID}
              className="pointer-events-none h-px w-full max-md:-mt-px"
              aria-hidden
            />
            <div
              className={cn(
                'relative z-20 bg-white pb-3 pt-1 md:pt-2',
                'sticky z-30 shadow-[0_1px_0_rgba(12,42,74,0.08)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/95',
                'md:-mx-6 md:px-6',
                'will-change-transform motion-reduce:transition-none transition-transform duration-200 ease-out',
                'translate-y-0',
              )}
              style={{ top: 'var(--nh-sticky-header-h, 0px)', color: data.textColor }}
            >
            <div className="flex items-start gap-3 px-3 pb-1 md:px-0">
              {(isDesktop || showFiltersForSSR) ? (
                <div className="relative shrink-0" ref={filterAnchorRef}>
                  <BrowseAllFiltersTab
                    disabled={listingFiltersSnapshot.length === 0}
                    isOpen={isBrowseFilterSheetOpen}
                    onToggle={trackAndToggleBrowseFilterSheet}
                  />
                {(isBrowseFilterSheetOpen || showFiltersForSSR) && (
                  <div
                    className="absolute left-0 top-full z-50 mt-1.5 w-[min(100vw-2rem,360px)]"
                    role="dialog"
                    aria-label="Filters"
                  >
                    {/* Upward carat: centered on 64px filter control; color matches panel header */}
                    <div className="pointer-events-none relative z-[1] h-2 w-full" aria-hidden>
                      <div
                        className="absolute left-8 top-0 h-0 w-0 -translate-x-1/2 border-x-[10px] border-b-[9px] border-x-transparent border-b-[#0c2a4a] drop-shadow-[0_-1px_1px_rgba(0,0,0,0.06)]"
                      />
                    </div>
                    <div className="-mt-px flex max-h-[min(70vh,560px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
                      <RecommendedBrowseFiltersPanel
                        className="max-h-[min(70vh,560px)] min-h-0"
                        filters={browsePreviewFilters}
                        selectedOptions={browseSelectedOptions}
                        applyUrl={browseApplyUrl}
                        isLoadingPreview={isBrowseFilterPreviewLoading}
                        isPendingCommit={isBrowseFacetPendingCommit}
                        filtersResetUrl={filtersResetUrlSnapshot}
                        onToggleOption={browseToggleOption}
                        onApply={handleBrowseFilterApply}
                        onReset={handleBrowseFilterReset}
                        showApplyButton={false}
                      />
                    </div>
                  </div>
                )}
                </div>
              ) : null}
              <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide pb-4">
                <div className="flex gap-3">
                  {tabs.map((tab) => (
                    <CategoryFilterTab
                      key={tab.tag ? `${tab.tag}-${tab.label}` : '__all__'}
                      tab={tab}
                      isAll={tab === allTab}
                      isActive={tab === allTab ? isAllSelected : isTagSelected(tab.tag)}
                      onSelect={() => handleChipActivate(tab.tag, tab.label)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-x-0 top-full h-5 bg-gradient-to-b from-white to-transparent" />
          </div>
          </>
        )}

        <div
          className={cn(
            'px-3 pb-8 pt-2 md:px-0',
            showMobileFloatingBarPadding && 'max-md:pb-[calc(2rem+3.5rem+env(safe-area-inset-bottom))]'
          )}
        >
          {isLoading ? (
            <div className={cn(productGridClass, 'gap-3')}>
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-md bg-gray-100"
                  style={{ aspectRatio: '3/4' }}
                />
              ))}
            </div>
          ) : products.length > 0 ? (
            isServer ? (
              <div className={productGridClass} style={{ gap: data.gap }}>
                 {products.map((product, index) => {
                   const rowIndex = Math.floor(index / columnCount) + 1;
                   const colIndex = (index % columnCount) + 1;
                   return (
                     <ProductCard
                       key={product.designId}
                       product={product}
                       onProductClick={(p) => trackProductClick(p, rowIndex, colIndex)}
                       onWishlistToggle={trackWishlistToggle}
                       onVideoCallCartAdd={trackVideoCallCartAdd}
                     />
                   );
                 })}
              </div>
            ) : (
              <div ref={gridRef}>
                <div
                  style={{
                    height: `${totalSize}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {virtualRows.map((virtualRow) => {
                    const start = virtualRow.index * columnCount;
                    const rowProducts = products.slice(start, start + columnCount);

                    return (
                      <div
                        key={virtualRow.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                          // Window virtualizer uses one grid row per slice; CSS grid gap does not space these stacks.
                          paddingBottom: virtualRow.index < rowCount - 1 ? data.gap : undefined,
                        }}
                      >
                        <div className={productGridClass} style={{ gap: data.gap }}>
                          {rowProducts.map((product, j) => {
                            const card = (
                              <ProductCard
                                key={product.designId}
                                product={product}
                                onProductClick={(p) => trackProductClick(p, virtualRow.index + 1, j + 1)}
                                onWishlistToggle={trackWishlistToggle}
                                onVideoCallCartAdd={trackVideoCallCartAdd}
                              />
                            );
                            return (
                              <PlpProductViewTracker
                                key={product.designId}
                                product={product}
                                scrollDepth={virtualRow.index + 1}
                                horizontalPosition={j + 1}
                                viewedDesignIds={viewedProductIds.current}
                                fullyVisible={false}
                                onProductViewed={trackProductViewed}
                              >
                                {card}
                              </PlpProductViewTracker>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : null}

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-6">
              {isLoadingMore && (
                <div className={cn('w-full', productGridClass, 'gap-3')}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-md bg-gray-100"
                      style={{ aspectRatio: '3/4' }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <RecommendedBrowseFilterSheet
        open={showFiltersForSSR || (isBrowseFilterSheetOpen && !isDesktop)}
        filters={browsePreviewFilters}
        selectedOptions={browseSelectedOptions}
        applyUrl={browseApplyUrl}
        isLoadingPreview={isBrowseFilterPreviewLoading}
        filtersResetUrl={filtersResetUrlSnapshot}
        onToggleOption={browseToggleOption}
        onApply={handleBrowseFilterApply}
        onReset={handleBrowseFilterReset}
        onClose={handleBrowseFilterClose}
      />
    </section>
  );
}
